import React, { createContext, useContext, useState, useCallback } from 'react';

interface TranslationContextType {
  currentLanguage: 'ES' | 'EN';
  setLanguage: (language: 'ES' | 'EN') => void;
  translateText: (text: string) => Promise<string>;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Cache for translated texts to avoid re-translating
const translationCache = new Map<string, string>();

// Simple detection for already English text
const isEnglish = (text: string): boolean => {
  const englishWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'let', 'talk', 'view', 'our', 'about', 'contact', 'home', 'products', 'services'];
  const lowerText = text.toLowerCase();
  return englishWords.some(word => lowerText.includes(` ${word} `) || lowerText.startsWith(`${word} `) || lowerText.endsWith(` ${word}`));
};

// Browser-based translation function
const translateWithBrowser = async (text: string, targetLang: string): Promise<string> => {
  try {
    // Check if browser supports translation
    if ('translate' in document.createElement('div')) {
      // Create a temporary element for translation
      const tempDiv = document.createElement('div');
      tempDiv.textContent = text;
      tempDiv.setAttribute('translate', 'yes');
      tempDiv.lang = 'es';
      
      // This is a simplified approach - in reality, browser translation 
      // happens automatically when user enables it in browser settings
      // For now, we'll return original text and let browser handle it
      return text;
    }
  } catch (error) {
    console.warn('Browser translation not available:', error);
  }
  
  // Fallback: return original text
  return text;
};

// Simple translation mappings for key UI elements
const commonTranslations: Record<string, string> = {
  // Navigation
  'Inicio': 'Home',
  'Sobre Nosotros': 'About Us',
  'Cursos': 'Courses',
  'Noticias & Blog': 'News & Blog',
  'Contacto': 'Contact',
  'Misión & Visión': 'Mission & Vision',
  'La Finca': 'The Farm',
  'Quiénes Somos': 'Who We Are',
  
  // Hero Section
  'Cultivamos salud': 'We cultivate health',
  'desde el suelo': 'from the soil',
  'Agricultura ecológica y alimentos orgánicos accesibles desde nuestra finca en Bétera, Valencia.': 'Ecological agriculture and accessible organic food from our farm in Bétera, Valencia.',
  'Descubre nuestros cursos →': 'Discover our courses →',
  'Nuestros productos ecológicos →': 'Our ecological products →',
  
  // Common phrases
  'Natural organic farm.': 'Natural organic farm.',
  'San Francisco': 'San Francisco',
  'Mon - Sat: 9am - 6pm': 'Mon - Sat: 9am - 6pm',
  'Español': 'Spanish',
  'English': 'English'
};

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<'ES' | 'EN'>('ES');

  const setLanguage = useCallback((language: 'ES' | 'EN') => {
    setCurrentLanguage(language);
  }, []);

  const translateText = useCallback(async (text: string): Promise<string> => {
    // If current language is Spanish, return original
    if (currentLanguage === 'ES') {
      return text;
    }

    // If text is already in English, return as-is
    if (isEnglish(text)) {
      return text;
    }

    // Check cache first
    const cacheKey = `${text}_EN`;
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey)!;
    }

    // Check common translations
    if (commonTranslations[text]) {
      translationCache.set(cacheKey, commonTranslations[text]);
      return commonTranslations[text];
    }

    // Try browser translation (simplified approach)
    try {
      const translated = await translateWithBrowser(text, 'en');
      translationCache.set(cacheKey, translated);
      return translated;
    } catch (error) {
      console.warn('Translation failed, returning original:', error);
      return text;
    }
  }, [currentLanguage]);

  const value: TranslationContextType = {
    currentLanguage,
    setLanguage,
    translateText
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export default TranslationContext;